import React, { PropsWithChildren, useCallback, useEffect, useRef } from "react"
import { PropsWithStyle } from "@rogue_4/types"
import anime from "animejs"
import classNames from "classnames"

import { SFX } from "../../utils/sound"

import styles from "./index.module.less"

type TProps = PropsWithChildren<
    PropsWithStyle<
        React.HTMLAttributes<HTMLDivElement> & {
            size?: "big" | "small"
            active: boolean
            invert?: boolean
        }
    >
>

const BUTTON_ANIMATION = {
    tail_bottom: {
        opacity: [0, 1],
        scaleX: [0.8, 1],
    },
    tail_top: {
        opacity: [0, 1],
        scaleX: [0.8, 1],
        translateX: ["-2rem", "0.375rem"],
        translateY: ["0.3125rem", "0.3125rem"],
        rotate: ["-2deg", "-2deg"],
    },
    tail_core: {
        opacity: [0, 1],
        scaleX: [0.8, 1],
        translateX: ["-2rem", "0.375rem"],
        translateY: ["0.25rem", "0.25rem"],
        rotate: ["-6deg", "-6deg"],
    },
}
const getReverse = (obj: Record<string, any[]>) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = [value[1], value[0]]
        return acc
    }, {})
}
const REVERSED_BUTTON_ANIMATION = Object.entries(BUTTON_ANIMATION).reduce(
    (acc, [key, value]) => {
        acc[key] = getReverse(value)
        return acc
    },
    {}
)
const getAnimationByIndex = (obj: Record<string, any[]>, index: number) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = value[index]
        return acc
    }, {})
}

const GameButton = React.forwardRef<HTMLDivElement, TProps>(
    (
        { className, style, size = "big", children, active, invert, ...props },
        ref
    ) => {
        const tailBottomRef = useRef<HTMLDivElement>(null)
        const tailTopRef = useRef<HTMLDivElement>(null)
        const tailCoreRef = useRef<HTMLDivElement>(null)
        const animeRef = useRef<anime.AnimeTimelineInstance>()
        const showTail = useCallback((immediate?: boolean) => {
            if (animeRef.current) {
                animeRef.current.pause()
            }

            const duration = immediate ? 0 : 500
            const easing = "easeOutCubic"
            animeRef.current = anime
                .timeline()
                .add({
                    targets: tailBottomRef.current,
                    ...getAnimationByIndex(BUTTON_ANIMATION["tail_bottom"], 1),
                    easing: easing,
                    duration: duration,
                    complete: () => {
                        tailBottomRef.current?.classList.add(
                            styles.loopAnimated
                        )
                    },
                })
                .add(
                    {
                        targets: tailTopRef.current,
                        ...getAnimationByIndex(BUTTON_ANIMATION["tail_top"], 1),
                        easing: easing,
                        duration: duration,
                        complete: () => {
                            tailTopRef.current?.classList.add(
                                styles.loopAnimated
                            )
                        },
                    },
                    0
                )
                .add(
                    {
                        targets: tailCoreRef.current,
                        ...getAnimationByIndex(
                            BUTTON_ANIMATION["tail_core"],
                            1
                        ),
                        easing: easing,
                        duration: duration,
                        complete: () => {
                            tailCoreRef.current?.classList.add(
                                styles.loopAnimated
                            )
                        },
                    },
                    0
                )
        }, [])
        const hideTail = useCallback((immediate?: boolean) => {
            const duration = immediate ? 0 : 300
            const easing = "easeOutCubic"
            if (animeRef.current) {
                animeRef.current.pause()
            }
            animeRef.current = anime
                .timeline()
                .add({
                    targets: tailBottomRef.current,
                    ...getAnimationByIndex(
                        REVERSED_BUTTON_ANIMATION["tail_bottom"],
                        1
                    ),
                    easing: easing,
                    duration: duration,
                    complete: () => {
                        tailBottomRef.current?.classList.remove(
                            styles.loopAnimated
                        )
                    },
                })
                .add(
                    {
                        targets: tailTopRef.current,
                        ...getAnimationByIndex(
                            REVERSED_BUTTON_ANIMATION["tail_top"],
                            1
                        ),
                        easing: easing,
                        duration: duration,
                        complete: () => {
                            tailTopRef.current?.classList.remove(
                                styles.loopAnimated
                            )
                        },
                    },
                    0
                )
                .add(
                    {
                        targets: tailCoreRef.current,
                        ...getAnimationByIndex(
                            REVERSED_BUTTON_ANIMATION["tail_core"],
                            1
                        ),
                        easing: easing,
                        duration: duration,
                        complete: () => {
                            tailCoreRef.current?.classList.remove(
                                styles.loopAnimated
                            )
                        },
                    },
                    0
                )
        }, [])

        const initRef = useRef(false)
        useEffect(() => {
            if (active) showTail(!initRef.current)
            else hideTail(!initRef.current)
            if (!initRef.current) initRef.current = true
        }, [active])
        return (
            <div
                {...props}
                className={classNames(
                    styles.gameButton,
                    styles[size],
                    // styles.active,
                    invert && styles.disabled,
                    className
                )}
                style={style}
                ref={ref}
                onClick={(e) => {
                    e.stopPropagation()
                    SFX.play("commonClick")
                    props.onClick?.(e)
                }}
            >
                <div className={styles.bg}>
                    <div
                        className={classNames(styles.bgShadow, styles.bgImage)}
                    ></div>
                    <div
                        className={classNames(
                            styles.tailComponent,
                            styles.bottom
                        )}
                        ref={tailBottomRef}
                    />
                    <div
                        className={classNames(styles.tailComponent, styles.top)}
                        ref={tailTopRef}
                    />
                    <div
                        className={classNames(
                            styles.bgTop,
                            invert && styles.invert,
                            styles.bgImage
                        )}
                    ></div>
                    <div
                        className={classNames(
                            styles.tailComponent,
                            styles.core
                        )}
                        ref={tailCoreRef}
                    />
                </div>
                <div
                    className={classNames(
                        styles.content,
                        invert && styles.invert
                    )}
                >
                    {children}
                </div>
            </div>
        )
    }
)

GameButton.displayName = "GameButton"

export default GameButton
